import type { Token } from "marked";

const MAGNIT_DEEPLINK_PROTOCOL = "magnit://";

type ReplacementConfig = {
  source: string;
  target: string;
  isFullReplace?: boolean; // если этот параметр true то target заменяет весь диплинк, иначе по-умолчанию только замена по префиксу аля replace(source, target)
};

// таблица замен немного простыня, но зато похоже по структуре и очередности на спеку,
// я считаю это в плюс, т.к. можно проще свериться на предмет отличий со спекой
// https://eportal.magnit.ru/pages/viewpage.action?pageId=174444489
const REPLACEMENTS: readonly Readonly<ReplacementConfig>[] = (
  [
    {
      source: "magnit://personalpromotions/promo/",
      target: "https://magnit.ru/profile/personal-promotions/",
    },
    {
      source: "magnit://discounts?category_id=",
      target: "https://magnit.ru/promo-catalog/",
    },
    {
      source: "magnit://transactions",
      target: "https://magnit.ru/profile/transactions",
      isFullReplace: true,
    },
    {
      source: "magnit://loyaltyCard ",
      target: "https://magnit.ru/profile",
      isFullReplace: true,
    },
    {
      source: "magnit://benefit/details",
      target: "https://magnit.ru/profile/mplus",
      isFullReplace: true,
    },
    {
      source: "magnit://personal-coupons/Mobile",
      target: "https://magnit.ru/profile/coupons",
      isFullReplace: true,
    },
    {
      source: "magnit://web?url=", // это диплин на веб ссылку
      target: "", // оно просто затрёт часть диплинка оставив только ссылку
      // в теории этот случай может сломаться если что-то нахитрожопят в диплинке, например:
      // magnit://web?utm_source=blah&url=....
      // но пока такой простой вариант оставлю, чтобы не усложнять механизм.
      // авось?
    },
    {
      source: "magnit://favorite_category/select",
      target: "https://magnit.ru/profile/favorite-categories",
      isFullReplace: true,
    },
    {
      source: "magnit://subscriptions",
      target: "https://my.magnit.ru/subscription",
      isFullReplace: true,
    },
    {
      // примечание, ссылки на конкретные клубы и офферы не поддерживаем,
      // все эти ссылки просто на нашу общую страницу клубов пересадит
      source: "magnit://clubs",
      target: "https://magnit.ru/clubs",
      isFullReplace: true,
    },
    {
      source: "magnit://settings",
      target: "https://magnit.ru/profile/edit",
      isFullReplace: true,
    },
    {
      source: "magnit://partner/",
      target: "https://magnit.ru/partners/",
    },
    {
      source: "magnit://catalog/category/",
      target: "https://magnit.ru/catalog/",
    },
    {
      source: "magnit://catalog/good/",
      target: "https://magnit.ru/product/",
    },
    {
      source: "magnit://dostavka/express/category/",
      target: "https://magnit.ru/catalog/",
    },
    {
      source: "magnit://dostavka/express/good/",
      target: "https://magnit.ru/product/",
    },
    {
      source: "magnit://market/catalog?category_id=",
      target: "https://mm.ru/category/",
    },
  ] satisfies ReplacementConfig[]
)
  .map(({ source, target, ...misc }) => ({
    ...misc,
    // чтобы работать в case-insensetive стиле алгоритм работает со строками в нижнем регистре.
    // вообще на практике нам норм строки и выпадают уже в нижнем регистре,
    // но кто знает что нам может попасться... поэтому подстраховываемся
    source: source.toLowerCase(),
    target: target.toLowerCase(),
  }))
  .sort((a, b) => {
    // предсортируем по длинейшему source,
    // чтобы наиболее конкретный реплейс пробовался первым,
    // иначе может зареплейсить неверно и частично и это не то что мы ожидаем
    //
    // Например очевидно что /catalog/category более специфично чем /catalog/.
    // И если мы сначала пройдемся реплейснем /catalog/ и на этом остановимся, то это не то что нам нужно.
    // Потому-что настоящий мэтч по /catalog/category уже останется неудовлетворённым.
    if (a.source.length === b.source.length) {
      return 0;
    }
    return a.source.length > b.source.length ? -1 : 1;
  });

const isMagnitDeeplink = (link: string) =>
  (link?.toLowerCase() || "").startsWith(MAGNIT_DEEPLINK_PROTOCOL);

const getReplacement = (link: string): string => {
  if (!isMagnitDeeplink(link)) {
    return "";
  }
  link = link?.toLowerCase() || "";
  for (const { source, target, isFullReplace } of REPLACEMENTS) {
    if (link.startsWith(source)) {
      return isFullReplace ? target : link.replace(source, target);
    }
  }
  return "";
};

const replaceInMarkdownLink = (token: Token): void => {
  if (token.type !== "link") {
    return;
  }
  const link = token.href;
  if (!isMagnitDeeplink(link)) {
    return;
  }
  const replacement = getReplacement(link);
  if (replacement) {
    // Да мутируем токен, да сама апиха marked так и расчитывает и позволяет.
    // Такая вот библиотека, старая дедовская, местами в мутабельном стиле.
    // Функциональщики, что с лицом?
    token.href = replacement;
  }
};

const replaceInHtml = (token: Token): void => {
  if (token.type !== "html") {
    return;
  }
  // Marked не желает парсить в отдельные токены тексте если это уже готовый html,
  // поэтому мы можем получить довольно большой кусок html на вход...
  // Нам надо выцепить все линки, пока-что решил пойти простым путём -- понадёюсь на регулярку,
  // однако это может вызвать нежелательные замены в обычных текстовых элементах например и т.п.
  // НО поидее всё должно быть окей, т.к. редко в обычном тексте кто будет писать href="...." вне контекста ссылки...
  // да ещё и с урлом именно похожим на диплинк...
  // Должно быть мифическое совпадение обстоятельств, чтобы этот хитрый частный случай стрельнул.
  // Поэтому в большинстве случаев должно работать окей
  token.text = token.text.replaceAll(
    /href\s*=\s*"(.*?)"/gi,
    (match: string, capture?: string) => {
      if (!capture || !isMagnitDeeplink(capture)) {
        return match;
      }
      const replacement = getReplacement(capture);
      if (!replacement) {
        return match;
      }
      return match.replace(capture, replacement);
    },
  );
};

export const walkTokensAndRewriteDeepLinks = (token: Token): void => {
  replaceInHtml(token);
  replaceInMarkdownLink(token);
};
